import React, { useEffect, useState } from 'react';
import {
  ChevronRight,
  CheckCircle,
  Download,
  Loader2,
  Calendar,
  MapPin,
  AlignLeft,
  User,
  Phone,
  Mail,
  Home,
  ArrowLeft,
} from 'lucide-react';
import type { ModelData, ParticipantRole, ReleaseRecord } from '@shared/types';
import SignaturePad from './SignaturePad';
import FinalContract from './FinalContract';
import { generatePdfBlob, blobToBase64 } from '../utils/pdfHelpers';
import { api } from '../api';
import { useLang, type DictKey } from '../i18n';

interface ModelFlowProps {
  release: ReleaseRecord;
  token: string;
}

const ROLE_BANNER_KEY: Record<ParticipantRole, DictKey> = {
  model: 'role.model',
  makeup_artist: 'role.makeup_artist',
  stylist: 'role.stylist',
  retoucher: 'role.retoucher',
  assistant: 'role.assistant',
  other: 'role.contributor',
};

const ModelFlow: React.FC<ModelFlowProps> = ({ release, token }) => {
  const { t, lang } = useLang();
  const alreadySigned = !!release.signedAt && !!release.signer;
  const [step, setStep] = useState<number>(alreadySigned ? 3 : 1);
  const [model, setModel] = useState<ModelData>(
    release.signer ?? {
      fullName: '',
      email: '',
      phone: '',
      dob: '',
      address: '',
      signatureDataUrl: '',
    },
  );
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [emailSent, setEmailSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const role = release.role || 'model';
  const isModel = role === 'model';

  const canProceedToSign = model.fullName && model.dob && model.phone;
  const canFinish = agreed && model.signatureDataUrl;

  useEffect(() => {
    if (alreadySigned) setSubmitStatus('success');
  }, [alreadySigned]);

  // Block tab close while the release is being submitted — without this, a
  // closed tab between PDF generation and the server PUT would leave the
  // signature lost with no record on the server.
  useEffect(() => {
    if (!submitting) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [submitting]);

  const handleSubmit = async () => {
    if (!canFinish) return;
    const signedAt = new Date().toLocaleDateString('en-US');
    const finalModel: ModelData = { ...model, signedAt };
    setModel(finalModel);
    setStep(3);
    setSubmitting(true);
    setErrorMsg(null);

    await new Promise((r) => setTimeout(r, 500));

    try {
      const blob = await generatePdfBlob('contract-preview');
      if (!blob) throw new Error('PDF generation failed');
      const pdfBase64 = await blobToBase64(blob);

      const res = await api.signRelease(token, { signer: finalModel, pdfBase64 });
      setEmailSent(res.emailSent);
      setSubmitStatus('success');
    } catch (e) {
      setSubmitStatus('error');
      setErrorMsg(e instanceof Error ? e.message : t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => window.print();

  const dateLocale = lang === 'ru' ? 'ru-RU' : 'en-GB';
  const formattedShootDate = new Date(release.shoot.date).toLocaleDateString(dateLocale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  if (step === 3) {
    return (
      <div className="min-h-screen bg-gray-100 pb-20">
        {submitting && (
          <div
            className="fixed inset-0 bg-black/65 backdrop-blur-sm z-50 flex items-center justify-center p-6 no-print"
            role="dialog"
            aria-modal="true"
            aria-live="polite"
          >
            <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Loader2 className="animate-spin text-indigo-600" size={32} />
                </div>
                <h2 className="text-xl font-bold text-gray-900">{t('sign.overlay.title')}</h2>
                <p className="text-sm text-gray-600 leading-relaxed">{t('sign.overlay.body')}</p>
                <p className="text-xs text-gray-400">{t('sign.overlay.timing')}</p>
              </div>
            </div>
          </div>
        )}
        <div className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white p-4 no-print sticky top-0 z-10 shadow-lg">
          <div className="max-w-4xl mx-auto flex flex-col gap-3">
            <div className="flex justify-between items-center flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {submitStatus === 'success' ? (
                  <>
                    <CheckCircle className="text-green-300" />
                    <span className="font-semibold text-base">{t('sign.success.title')}</span>
                  </>
                ) : submitStatus === 'error' ? (
                  <span className="text-red-200 font-semibold">{t('sign.success.error')}</span>
                ) : (
                  <>
                    <Loader2 className="animate-spin" />
                    <span className="font-semibold">{t('sign.signing')}</span>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2 text-sm bg-white/15 px-3 py-1 rounded-full backdrop-blur">
                {submitting && (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>{t('sign.success.sending')}</span>
                  </>
                )}
                {!submitting && submitStatus === 'success' && (
                  <>
                    <CheckCircle size={14} className="text-green-300" />
                    <span>{emailSent ? t('sign.success.toPhotographer') : t('sign.success.saved')}</span>
                  </>
                )}
                {!submitting && submitStatus === 'error' && (
                  <span className="text-red-200">{errorMsg ?? t('common.error')}</span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={handlePrint}
                className="bg-white text-indigo-900 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-gray-100 transition shadow-md"
              >
                <Download size={16} /> {t('sign.downloadPdf')}
              </button>
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-center">
          <FinalContract release={release} model={model} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-12 relative">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-center py-6 px-4 shadow-md">
        <p className="text-[11px] opacity-80 uppercase tracking-widest">{t('sign.role.banner')}</p>
        <h2 className="text-2xl font-bold tracking-wider serif mt-1">
          {t(ROLE_BANNER_KEY[role]).toUpperCase()}
        </h2>
      </div>

      <StepIndicator step={step} t={t} />

      <div className="px-5 md:px-8">
        <div className="mb-6 bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <h1 className="text-2xl font-bold text-gray-900 serif mb-3">{release.shoot.title}</h1>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-600 text-sm">
            <DetailItem icon={<Calendar size={15} />} text={formattedShootDate} />
            <DetailItem icon={<MapPin size={15} />} text={release.shoot.location || '—'} />
            {release.shoot.description && (
              <div className="md:col-span-2 flex items-start gap-2 pt-3 border-t border-gray-100">
                <AlignLeft size={15} className="text-indigo-500 mt-0.5 flex-shrink-0" />
                <p className="italic text-gray-700">"{release.shoot.description}"</p>
              </div>
            )}
          </dl>
        </div>

        {step === 1 && (
          <div className="space-y-5">
            <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100 text-sm text-indigo-900">
              {isModel ? t('sign.intro.model') : t('sign.intro.contributor')}
            </div>

            <div className="space-y-3">
              <SignField
                icon={<User size={15} />}
                label={t('sign.field.fullName')}
                value={model.fullName}
                onChange={(v) => setModel({ ...model, fullName: v })}
                placeholder={t('sign.placeholder.fullName')}
              />

              <div className="grid grid-cols-2 gap-3">
                <SignField
                  icon={<Calendar size={15} />}
                  label={t('sign.field.dob')}
                  type="date"
                  value={model.dob}
                  onChange={(v) => setModel({ ...model, dob: v })}
                />
                <SignField
                  icon={<Phone size={15} />}
                  label={t('sign.field.phone')}
                  type="tel"
                  value={model.phone}
                  onChange={(v) => setModel({ ...model, phone: v })}
                  placeholder={t('sign.placeholder.phone')}
                />
              </div>

              <SignField
                icon={<Mail size={15} />}
                label={t('sign.field.email')}
                type="email"
                value={model.email}
                onChange={(v) => setModel({ ...model, email: v })}
                placeholder={t('sign.placeholder.email')}
              />

              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                  <Home size={15} className="text-gray-400" /> {t('sign.field.address')}
                </label>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none h-20 resize-none transition"
                  value={model.address}
                  onChange={(e) => setModel({ ...model, address: e.target.value })}
                  placeholder={t('sign.placeholder.address')}
                />
              </div>
            </div>

            <button
              disabled={!canProceedToSign}
              onClick={() => setStep(2)}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-xl font-bold hover:opacity-95 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed transition flex items-center justify-center gap-2 shadow-lg shadow-indigo-200"
            >
              {t('sign.next')} <ChevronRight size={18} />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 max-h-72 overflow-y-auto">
              <h3 className="font-bold text-gray-900 mb-2">{t('sign.summary.title')}</h3>
              <p className="text-xs text-gray-500 mb-3">{t('sign.summary.fullText')}</p>
              <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-700 leading-relaxed">
                {isModel ? (
                  <>
                    <li>
                      {lang === 'ru' ? (
                        <>
                          Я предоставляю <strong>{release.photographer.name}</strong> и его лицензиатам
                          безотзывное всемирное бессрочное безвозмездное право использовать моё имя,
                          изображение и фотографии, сделанные в рамках{' '}
                          <strong>{release.shoot.title}</strong>.
                        </>
                      ) : (
                        <>
                          I grant <strong>{release.photographer.name}</strong> and their licensees the
                          irrevocable, worldwide, perpetual, royalty-free right to use my name,
                          likeness, and the photographs taken in connection with{' '}
                          <strong>{release.shoot.title}</strong>.
                        </>
                      )}
                    </li>
                    <li>
                      {lang === 'ru' ? (
                        <>
                          Объём использования:{' '}
                          {release.shoot.isCommercial ? (
                            <>
                              редакционные публикации (журналы, книги, газеты, сайты){' '}
                              <strong>и</strong> коммерческое использование (реклама, упаковка,
                              стоки, мерчандайз, синдикация).
                            </>
                          ) : (
                            <>
                              только редакционно — журналы, газеты, книги, журналистские сайты,
                              выставки, портфолио, редакционная синдикация. Коммерческое
                              использование требует отдельного согласия.
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          Usage scope:{' '}
                          {release.shoot.isCommercial ? (
                            <>
                              editorial publications (magazines, books, newspapers, websites){' '}
                              <strong>and</strong> commercial use (advertising, packaging, stock,
                              merchandise, syndication).
                            </>
                          ) : (
                            <>
                              editorial only — magazines, newspapers, books, journalistic websites,
                              exhibitions, portfolio, and editorial syndication. Commercial use
                              requires my separate consent.
                            </>
                          )}
                        </>
                      )}
                    </li>
                    <li>
                      {lang === 'ru'
                        ? 'Фотограф может кадрировать, ретушировать, комбинировать, добавлять подписи и публиковать фотографии с моим именем или без него.'
                        : 'The photographer may crop, retouch, composite, caption, and publish the photographs with or without my name.'}
                    </li>
                    <li>
                      {lang === 'ru'
                        ? 'Я отказываюсь от права инспекции и одобрения фотографий до публикации, а также от роялти и дополнительной компенсации.'
                        : 'I waive any right to inspect or approve the photographs before publication and any claim to royalties or further compensation.'}
                    </li>
                    <li>
                      {lang === 'ru'
                        ? 'Я освобождаю фотографа и лицензиатов от претензий по приватности, праву публичности, клевете, ложному свету и аналогичным.'
                        : 'I release the photographer and licensees from claims for privacy, right of publicity, defamation, false light, and similar.'}
                    </li>
                    <li>
                      {lang === 'ru' ? (
                        <>
                          Обучение AI/ML моделей и генерация синтетических медиа{' '}
                          <strong>исключены</strong> из этого согласия, если не оговорено отдельно.
                        </>
                      ) : (
                        <>
                          AI / machine-learning training and synthetic-media generation are{' '}
                          <strong>excluded</strong> unless separately agreed.
                        </>
                      )}
                    </li>
                    <li>
                      {lang === 'ru'
                        ? 'Мне исполнилось 18 лет, я в здравом уме и не связан(а) конфликтующим эксклюзивным контрактом.'
                        : 'I am at least 18 years old, of sound mind, and not bound by any conflicting exclusive contract.'}
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      {lang === 'ru' ? (
                        <>
                          Моя работа над <strong>{release.shoot.title}</strong> является work for
                          hire. В части в которой это не так — я уступаю все права (авторские,
                          смежные, связанные) фотографу{' '}
                          <strong>{release.photographer.name}</strong> на весь мир и бессрочно.
                        </>
                      ) : (
                        <>
                          My contributions to <strong>{release.shoot.title}</strong> are work made
                          for hire. To the extent they are not, I assign all rights — copyright,
                          neighboring, and related — to <strong>{release.photographer.name}</strong>{' '}
                          worldwide and in perpetuity.
                        </>
                      )}
                    </li>
                    <li>
                      {lang === 'ru'
                        ? 'Фотограф может использовать результирующие фотографии в любых медиа — редакционно или коммерчески: журналы, реклама, стоки, мерчандайз.'
                        : 'The photographer may use the resulting photographs in any media, editorial or commercial, including magazines, advertising, stock, and merchandise.'}
                    </li>
                    <li>
                      {lang === 'ru'
                        ? 'Я отказываюсь от моральных прав (атрибуция и неприкосновенность) в максимально допустимом законом объёме.'
                        : 'I waive moral rights (attribution and integrity) to the maximum extent permitted by law.'}
                    </li>
                    <li>
                      {lang === 'ru'
                        ? 'Я отказываюсь от права одобрения и от претензий на роялти или дополнительную компенсацию.'
                        : 'I waive any right of approval and any claim to royalties or further compensation.'}
                    </li>
                    <li>
                      {lang === 'ru'
                        ? 'Мой вклад оригинален и не нарушает прав третьих лиц.'
                        : 'My contributions are original and do not infringe any third-party rights.'}
                    </li>
                    <li>
                      {lang === 'ru'
                        ? 'Мне исполнилось 18 лет и я обладаю полной правоспособностью.'
                        : 'I am at least 18 years old and have full legal capacity.'}
                    </li>
                  </>
                )}
              </ul>
            </div>

            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-800">{t('sign.agreement')}</span>
            </label>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">{t('sign.signHere')}</label>
              <SignaturePad onEnd={(data) => setModel({ ...model, signatureDataUrl: data || '' })} />
              <p className="text-xs text-gray-400 mt-1">{t('sign.signHere.hint')}</p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-3 border border-gray-300 text-gray-600 rounded-xl font-semibold hover:bg-gray-50 transition flex items-center justify-center gap-2"
              >
                <ArrowLeft size={16} /> {t('common.back')}
              </button>
              <button
                disabled={!canFinish}
                onClick={handleSubmit}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-bold hover:opacity-95 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed transition shadow-lg shadow-indigo-200"
              >
                {t('sign.complete')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StepIndicator: React.FC<{ step: number; t: (k: DictKey) => string }> = ({ step, t }) => {
  const steps: { n: number; key: DictKey }[] = [
    { n: 1, key: 'sign.step.info' },
    { n: 2, key: 'sign.step.review' },
    { n: 3, key: 'sign.step.done' },
  ];
  return (
    <div className="bg-white border-b border-gray-200">
      <div className="max-w-2xl mx-auto px-5 py-3 flex items-center gap-2">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition ${
                  step === s.n
                    ? 'bg-indigo-600 text-white'
                    : step > s.n
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step > s.n ? <CheckCircle size={14} /> : s.n}
              </div>
              <span
                className={`text-xs font-medium hidden sm:inline ${
                  step >= s.n ? 'text-gray-900' : 'text-gray-400'
                }`}
              >
                {t(s.key)}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 transition ${
                  step > s.n ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const DetailItem: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <div className="flex items-center gap-2">
    <span className="text-indigo-500">{icon}</span>
    <span>{text}</span>
  </div>
);

interface SignFieldProps {
  icon?: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}

const SignField: React.FC<SignFieldProps> = ({ icon, label, value, onChange, placeholder, type = 'text' }) => (
  <div>
    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
      {icon && <span className="text-gray-400">{icon}</span>}
      {label}
    </label>
    <input
      type={type}
      className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </div>
);

export default ModelFlow;
